export const DESCRIPTION_CHARACTER_LIMIT = 2000;

export type ApplyLimitedInsertArgs = {
  value: string;
  selectionStart: number;
  selectionEnd: number;
  insertText: string;
  limit: number;
};

export type ApplyLimitedInsertResult = {
  nextValue: string;
  cursor: number;
  clipped: boolean;
  /** Characters from insertText that fit within the limit (may be empty). */
  fittedInsert: string;
  /** Whether any of the insertText could be applied within the limit. */
  canInsert: boolean;
};

/**
 * Applies an insert (or selection replacement) under a hard character limit.
 * Does not mutate the original value when the insert does not fit at all.
 */
export function applyLimitedInsert({
  value,
  selectionStart,
  selectionEnd,
  insertText,
  limit
}: ApplyLimitedInsertArgs): ApplyLimitedInsertResult {
  const start = Math.max(0, Math.min(selectionStart, value.length));
  const end = Math.max(start, Math.min(selectionEnd, value.length));
  const before = value.slice(0, start);
  const after = value.slice(end);
  const room = Math.max(0, limit - before.length - after.length);

  if (insertText.length <= room) {
    const nextValue = before + insertText + after;
    return {
      nextValue,
      cursor: start + insertText.length,
      clipped: false,
      fittedInsert: insertText,
      canInsert: true
    };
  }

  const fittedInsert = insertText.slice(0, room);
  if (fittedInsert.length === 0) {
    return {
      nextValue: value,
      cursor: end,
      clipped: true,
      fittedInsert: '',
      canInsert: false
    };
  }

  const nextValue = before + fittedInsert + after;
  return {
    nextValue,
    cursor: start + fittedInsert.length,
    clipped: true,
    fittedInsert,
    canInsert: true
  };
}

export function wouldExceedLimit(
  value: string,
  selectionStart: number,
  selectionEnd: number,
  insertText: string,
  limit: number
): boolean {
  return applyLimitedInsert({
    value,
    selectionStart,
    selectionEnd,
    insertText,
    limit
  }).clipped;
}
